import { base, TABLES, FIELDS } from '../config';

export class BlogPostModel {
  static get table() {
    if (!base) {
      throw new Error('Airtable is not properly configured. Please check your environment variables.');
    }
    return base(TABLES.BLOG_POSTS);
  }

  static fields = FIELDS.BLOG_POSTS;

  static async getFeaturedPost() {
    try {
      console.log('Starting getFeaturedPost...');
      console.log('Table name:', TABLES.BLOG_POSTS);
      console.log('Fields:', this.fields);
      
      if (!base) {
        console.error('Airtable base is not configured');
        console.log('Environment variables:', {
          baseId: process.env.REACT_APP_AIRTABLE_BASE_ID ? 'Set' : 'Not Set',
          token: process.env.REACT_APP_AIRTABLE_TOKEN ? 'Set' : 'Not Set'
        });
        throw new Error('Unable to fetch blog post. Please try again later.');
      }

      // First try to get a featured post that's published
      const featuredFormula = `AND({${this.fields.STATUS}} = 'Published', 
                                {${this.fields.FEATURED}})`;
      console.log('Searching for featured posts with formula:', featuredFormula);
      
      const featuredRecords = await this.table.select({
        maxRecords: 1,
        filterByFormula: featuredFormula,
        sort: [{ field: this.fields.PUBLISH_DATE, direction: 'desc' }]
      }).firstPage();

      console.log('Featured posts found:', featuredRecords.length);
      
      // If we found a featured post, return it
      if (featuredRecords.length > 0) {
        const featuredPost = this.formatRecord(featuredRecords[0]);
        console.log('Returning featured post:', {
          title: featuredPost.title,
          status: featuredPost.status,
          featured: featuredPost.featured,
          publishDate: featuredPost.publishDate
        });
        return featuredPost;
      }

      // If no featured post, fall back to most recent published post
      const recentFormula = `{${this.fields.STATUS}} = 'Published'`;
      console.log('No featured post found. Searching for recent posts with formula:', recentFormula);
      
      const records = await this.table.select({
        maxRecords: 1,
        filterByFormula: recentFormula,
        sort: [{ field: this.fields.PUBLISH_DATE, direction: 'desc' }]
      }).firstPage();

      console.log('Recent posts found:', records.length);

      if (records.length === 0) {
        console.log('No published posts found');
        return null;
      }

      const recentPost = this.formatRecord(records[0]);
      console.log('Returning most recent post:', {
        title: recentPost.title,
        status: recentPost.status,
        featured: recentPost.featured,
        publishDate: recentPost.publishDate
      });
      return recentPost;

    } catch (error) {
      console.error('Error in getFeaturedPost:', error);
      console.error('Error details:', {
        message: error.message,
        stack: error.stack,
        name: error.name
      });
      throw error;
    }
  }

  static async getNonFeaturedPosts() {
    try {
      const formula = `AND({${FIELDS.BLOG_POSTS.STATUS}} = 'Published', 
                        NOT({${FIELDS.BLOG_POSTS.FEATURED}}))`;
      
      const records = await base(TABLES.BLOG_POSTS)
        .select({
          filterByFormula: formula,
          sort: [{ field: FIELDS.BLOG_POSTS.PUBLISH_DATE, direction: 'desc' }]
        })
        .firstPage();

      return records.map(record => ({
        id: record.id,
        title: record.get(FIELDS.BLOG_POSTS.TITLE),
        subtitle: record.get(FIELDS.BLOG_POSTS.SUBTITLE),
        content: record.get(FIELDS.BLOG_POSTS.CONTENT),
        category: record.get(FIELDS.BLOG_POSTS.CATEGORY),
        publishDate: record.get(FIELDS.BLOG_POSTS.PUBLISH_DATE),
        readTime: record.get(FIELDS.BLOG_POSTS.READ_TIME),
        titleImageUrl: record.get(FIELDS.BLOG_POSTS.TITLE_IMAGE),
        author: record.get(FIELDS.BLOG_POSTS.AUTHOR),
        slug: record.get(FIELDS.BLOG_POSTS.SLUG),
        tags: record.get(FIELDS.BLOG_POSTS.TAGS),
        seoDescription: record.get(FIELDS.BLOG_POSTS.SEO_DESCRIPTION),
        lastModified: record.get(FIELDS.BLOG_POSTS.LAST_MODIFIED)
      }));
    } catch (error) {
      console.error('Error in getNonFeaturedPosts:', error);
      console.error('Error details:', {
        message: error.message,
        stack: error.stack,
        name: error.name
      });
      throw error;
    }
  }

  static async getAllPublished() {
    try {
      console.log('Starting getAllPublished...');
      
      if (!base) {
        console.error('Airtable is not configured');
        throw new Error('Unable to fetch blog posts. Please try again later.');
      }

      const formula = `{${this.fields.STATUS}} = 'Published'`;
      console.log('Fetching all published posts with formula:', formula);

      const records = await this.table.select({
        filterByFormula: formula,
        sort: [{ field: this.fields.PUBLISH_DATE, direction: 'desc' }]
      }).all();

      console.log('Total published posts found:', records.length);
      return records.map(record => this.formatRecord(record));
    } catch (error) {
      console.error('Error in getAllPublished:', error);
      throw error;
    }
  }

  static formatRecord(record) {
    try {
      const formatted = {
        id: record.id,
        title: record.get(this.fields.TITLE),
        subtitle: record.get(this.fields.SUBTITLE),
        content: record.get(this.fields.CONTENT),
        category: record.get(this.fields.CATEGORY),
        publishDate: record.get(this.fields.PUBLISH_DATE),
        readTimeMinutes: record.get(this.fields.READ_TIME),
        titleImageUrl: record.get(this.fields.TITLE_IMAGE),
        author: record.get(this.fields.AUTHOR),
        slug: record.get(this.fields.SLUG),
        status: record.get(this.fields.STATUS),
        tags: record.get(this.fields.TAGS),
        seoDescription: record.get(this.fields.SEO_DESCRIPTION),
        lastModified: record.get(this.fields.LAST_MODIFIED),
        featured: record.get(this.fields.FEATURED)
      };
      console.log('Formatted record:', {
        id: formatted.id,
        title: formatted.title,
        status: formatted.status,
        featured: formatted.featured
      });
      return formatted;
    } catch (error) {
      console.error('Error formatting record:', error);
      console.error('Record data:', record);
      throw error;
    }
  }
}
